/**
 * The three key-encryption keys.
 *
 *   KEK-P  passphrase + Argon2id. Mandatory, and the only one that can restore
 *          a vault onto a device that has never seen it.
 *   KEK-B  WebAuthn PRF. Convenience; bound to one authenticator on one device.
 *   KEK-S  a 30-minute session cache. Convenience; survives a reload, not a day.
 *
 * KEK-B and KEK-S can unlock hint levels 1–3. Level 4, level 5 and the exact
 * location always require KEK-P, freshly entered — see gates.ts. A convenience
 * key is exactly as strong as the device it is sitting on, and the deepest
 * hints are the ones worth stealing.
 */

import { importAesKey } from './aead.js';
import { fromBase64, toBase64 } from './bytes.js';
import { deriveKekBytes, type KdfParams } from './kdf.js';
import { normalisePassphrase } from './passphrase.js';

// ---------------------------------------------------------------------------
// KEK-P
// ---------------------------------------------------------------------------

export async function deriveKekP(
  passphrase: string,
  saltHex: string,
  params: KdfParams,
): Promise<CryptoKey> {
  const raw = await deriveKekBytes(normalisePassphrase(passphrase), saltHex, params);
  const key = await importAesKey(raw);
  raw.fill(0);
  return key;
}

// ---------------------------------------------------------------------------
// KEK-B — WebAuthn PRF
// ---------------------------------------------------------------------------

/** Fixed PRF input. Distinct per purpose so one credential can serve several. */
const PRF_SALT = new TextEncoder().encode('recovery-drill/vault-kek/v1');

export function isWebAuthnAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator.credentials?.create === 'function'
  );
}

interface PrfExtensionResults {
  prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } };
}

/**
 * Enrols a resident credential and reports whether the authenticator actually
 * supports PRF. Plenty of authenticators say yes to `create` and then hand back
 * `prf.enabled === false`; treating that as success would strand the user with
 * a biometric button that can never unwrap anything.
 */
export async function enrolBiometric(
  userId: Uint8Array,
  userName: string,
): Promise<{ credentialId: string } | null> {
  if (!isWebAuthnAvailable()) return null;

  const challenge = new Uint8Array(32);
  crypto.getRandomValues(challenge);

  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: challenge as BufferSource,
      rp: { name: 'Recovery Drill', id: window.location.hostname },
      user: {
        id: userId as BufferSource,
        name: userName,
        displayName: userName,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        residentKey: 'required',
        userVerification: 'required',
      },
      timeout: 60_000,
      attestation: 'none',
      extensions: { prf: { eval: { first: PRF_SALT as BufferSource } } },
    } as PublicKeyCredentialCreationOptions,
  })) as PublicKeyCredential | null;

  if (!credential) return null;
  const ext = credential.getClientExtensionResults() as PrfExtensionResults;
  if (!ext.prf?.enabled) return null;

  return { credentialId: toBase64(new Uint8Array(credential.rawId)) };
}

/** Runs the PRF and stretches its output into a KEK. Null if unavailable. */
export async function deriveKekB(credentialId: string): Promise<CryptoKey | null> {
  if (!isWebAuthnAvailable()) return null;

  const challenge = new Uint8Array(32);
  crypto.getRandomValues(challenge);

  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: challenge as BufferSource,
      allowCredentials: [
        { type: 'public-key', id: fromBase64(credentialId) as BufferSource },
      ],
      userVerification: 'required',
      timeout: 60_000,
      extensions: { prf: { eval: { first: PRF_SALT as BufferSource } } },
    } as PublicKeyCredentialRequestOptions,
  })) as PublicKeyCredential | null;

  if (!assertion) return null;
  const first = (assertion.getClientExtensionResults() as PrfExtensionResults).prf?.results
    ?.first;
  if (!first) return null;

  // PRF output is uniformly random but is not an AES key; run it through HKDF
  // so the KEK is domain-separated from anything else the credential signs.
  const ikm = await crypto.subtle.importKey('raw', first, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32) as BufferSource,
      info: PRF_SALT as BufferSource,
    },
    ikm,
    256,
  );
  return importAesKey(new Uint8Array(bits));
}

// ---------------------------------------------------------------------------
// KEK-S — 30 minute session cache
// ---------------------------------------------------------------------------

export const SESSION_TTL_MS = 30 * 60 * 1000;

const SESSION_KEY_STORAGE = 'rd.session.kek';
const SESSION_EXPIRY_STORAGE = 'rd.session.exp';

/**
 * The session KEK lives in sessionStorage, which means it is reachable by any
 * script that runs on this origin. That is an accepted trade: it unlocks hints
 * 1–3 only, it dies with the tab, and the CSP has no third-party script origins
 * for an attacker to arrive through.
 */
export function startSession(): Uint8Array {
  const raw = new Uint8Array(32);
  crypto.getRandomValues(raw);
  sessionStorage.setItem(SESSION_KEY_STORAGE, toBase64(raw));
  sessionStorage.setItem(SESSION_EXPIRY_STORAGE, String(Date.now() + SESSION_TTL_MS));
  return raw;
}

export function readSessionKekRaw(now = Date.now()): Uint8Array | null {
  const stored = sessionStorage.getItem(SESSION_KEY_STORAGE);
  const expiry = Number(sessionStorage.getItem(SESSION_EXPIRY_STORAGE) ?? '0');
  if (!stored || !Number.isFinite(expiry) || expiry <= now) {
    endSession();
    return null;
  }
  return fromBase64(stored);
}

export async function readSessionKek(now = Date.now()): Promise<CryptoKey | null> {
  const raw = readSessionKekRaw(now);
  if (!raw) return null;
  const key = await importAesKey(raw);
  raw.fill(0);
  return key;
}

export function sessionExpiresAt(): number | null {
  const expiry = Number(sessionStorage.getItem(SESSION_EXPIRY_STORAGE) ?? '0');
  return Number.isFinite(expiry) && expiry > 0 ? expiry : null;
}

export function endSession(): void {
  sessionStorage.removeItem(SESSION_KEY_STORAGE);
  sessionStorage.removeItem(SESSION_EXPIRY_STORAGE);
}
